import React from 'react'
import { getHighlightColor } from './constants'
import { BoundingBox, HighlightData } from './types'

export type OCRHighlightOverlayProps = {
  highlights: HighlightData[]
  imageIndex: number
  naturalWidth: number
  naturalHeight: number
}

const getRectFromBox = (box: BoundingBox) => {
  const xs = box.vertices.map((v) => v.x)
  const ys = box.vertices.map((v) => v.y)
  const x = Math.min(...xs)
  const y = Math.min(...ys)
  const width = Math.max(...xs) - x
  const height = Math.max(...ys) - y
  return { x, y, width, height }
}

const getConnectorLine = (
  readItemBox: BoundingBox,
  valueBox: BoundingBox
): { x1: number; y1: number; x2: number; y2: number } => {
  const readRect = getRectFromBox(readItemBox)
  const valueRect = getRectFromBox(valueBox)
  // 両矩形の相対位置に応じて接続点を動的に決定する
  // readItemBox が valueBox の左にある場合: read右端 → value左端
  // readItemBox が valueBox の右にある場合: read左端 → value右端
  const readIsLeft = readRect.x + readRect.width <= valueRect.x
  return {
    x1: readIsLeft ? readRect.x + readRect.width : readRect.x,
    y1: readRect.y + readRect.height / 2,
    x2: readIsLeft ? valueRect.x : valueRect.x + valueRect.width,
    y2: valueRect.y + valueRect.height / 2,
  }
}

const OCRHighlightOverlayBase: React.FC<OCRHighlightOverlayProps> = ({
  highlights,
  imageIndex,
  naturalWidth,
  naturalHeight,
}) => {
  if (naturalWidth <= 0 || naturalHeight <= 0) return null

  const filtered = highlights.filter((h) => h.imageIndex === imageIndex)
  if (filtered.length === 0) return null

  return (
    <svg
      aria-hidden="true"
      viewBox={`0 0 ${naturalWidth} ${naturalHeight}`}
      preserveAspectRatio="xMinYMin meet"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      {filtered.map((highlight) => {
        const color = getHighlightColor(highlight.colorIndex)

        return (
          <g
            key={`${highlight.ocrFormatId}-${highlight.imageIndex ?? 'null'}-${highlight.colorIndex}`}
          >
            {highlight.readItemBox &&
              (() => {
                const rect = getRectFromBox(highlight.readItemBox)
                return (
                  <rect
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    fill={color.fill}
                    stroke={color.border}
                    strokeWidth={2}
                  />
                )
              })()}
            {highlight.valueBox &&
              (() => {
                const rect = getRectFromBox(highlight.valueBox)
                return (
                  <rect
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    fill={color.fill}
                    stroke={color.border}
                    strokeWidth={2}
                  />
                )
              })()}
            {highlight.readItemBox &&
              highlight.valueBox &&
              (() => {
                const line = getConnectorLine(
                  highlight.readItemBox,
                  highlight.valueBox
                )
                return (
                  <line
                    x1={line.x1}
                    y1={line.y1}
                    x2={line.x2}
                    y2={line.y2}
                    stroke={color.border}
                    strokeWidth={1}
                    strokeDasharray="4 2"
                    opacity={0.5}
                  />
                )
              })()}
          </g>
        )
      })}
    </svg>
  )
}

const OCRHighlightOverlay = React.memo(OCRHighlightOverlayBase)
OCRHighlightOverlay.displayName = 'OCRHighlightOverlay'

export default OCRHighlightOverlay
export {
  type HighlightData,
  type BoundingBox,
  type BoundingBoxVertex,
} from './types'
